import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, MapPin } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useRegions, STATE_LEVEL } from '../lib/taxonomy';
import { ArticleFeed } from '../components/news/ArticleFeed';
import { FilterChips } from '../components/news/FilterChips';
import branding from '../config/branding';

/**
 * Rajya/Sahar: all UP news, filtered by mandal and then by district (city).
 *   /rajya                      all UP news
 *   /rajya/state                statewide news (no single district)
 *   /rajya/:division            one mandal
 *   /rajya/:division/:district  one district
 */
export default function RegionPage() {
  const { division, district } = useParams();
  const { isHindi } = useLanguage();
  const { regions } = useRegions();

  const isState = division === STATE_LEVEL;
  const currentDivision = !isState ? regions.divisions.find((d) => d.id === division) : null;
  const currentDistrict = currentDivision?.districts.find((d) => d.id === district);
  const name = (item) => (isHindi ? item.name_hi : item.name);

  const params = isState
    ? { district: STATE_LEVEL }
    : district
      ? { district }
      : division
        ? { division }
        : { region: 'up' };

  const heading = currentDistrict
    ? name(currentDistrict)
    : currentDivision
      ? `${name(currentDivision)} ${isHindi ? 'मंडल' : 'Mandal'}`
      : isState
        ? (isHindi ? 'राज्य स्तर की खबरें' : 'State news')
        : (isHindi ? 'राज्य/शहर' : 'Rajya/Sahar');

  const divisionChips = [
    { key: 'all', label: isHindi ? 'पूरा प्रदेश' : 'All UP', to: '/rajya', active: !division },
    { key: STATE_LEVEL, label: isHindi ? 'राज्य स्तर' : 'State level', to: `/rajya/${STATE_LEVEL}`, active: isState },
    ...regions.divisions.map((d) => ({
      key: d.id,
      label: name(d),
      to: `/rajya/${d.id}`,
      active: d.id === division,
    })),
  ];

  const districtChips = currentDivision
    ? [
        {
          key: 'all',
          label: isHindi ? 'पूरा मंडल' : 'Whole mandal',
          to: `/rajya/${currentDivision.id}`,
          active: !district,
        },
        ...currentDivision.districts.map((d) => ({
          key: d.id,
          label: name(d),
          to: `/rajya/${currentDivision.id}/${d.id}`,
          active: d.id === district,
        })),
      ]
    : null;

  return (
    <div className="min-h-screen bg-white" data-testid="region-page">
      <Helmet>
        <title>{`${heading} ${isHindi ? 'समाचार' : 'News'} | ${isHindi ? branding.nameHi : branding.name}`}</title>
        <meta
          name="description"
          content={isHindi ? `${heading} की ताज़ा खबरें।` : `Latest news from ${heading}, Uttar Pradesh.`}
        />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 pt-4 sm:pt-6">
        <Link
          to={currentDistrict ? `/rajya/${currentDivision.id}` : division ? '/rajya' : '/'}
          className="inline-flex items-center gap-0.5 min-h-[40px] kicker text-[var(--ink-muted)] hover:text-[var(--ink)]"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          <span className={isHindi ? 'font-hindi' : ''}>{isHindi ? 'वापस' : 'Back'}</span>
        </Link>
        <div className="pb-4 border-b border-[var(--ink)]">
          <h1 className={`flex items-center gap-2 text-3xl sm:text-5xl font-bold text-[var(--ink)] ${isHindi ? 'font-hindi-heading' : 'font-heading'}`}>
            <MapPin className="w-6 h-6 sm:w-8 sm:h-8 shrink-0" />
            {heading}
          </h1>
          {currentDistrict && (
            <p className={`story-meta mt-2 ${isHindi ? 'font-hindi' : ''}`}>
              {name(currentDivision)} {isHindi ? 'मंडल' : 'Mandal'}
            </p>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-5 sm:py-8 space-y-3">
        <p className={`kicker text-[var(--ink-muted)] ${isHindi ? 'font-hindi' : ''}`}>
          {isHindi ? 'मंडल चुनें' : 'Choose mandal'}
        </p>
        <FilterChips items={divisionChips} label={isHindi ? 'मंडल' : 'Mandals'} />

        {districtChips && (
          <>
            <p className={`pt-2 kicker text-[var(--ink-muted)] ${isHindi ? 'font-hindi' : ''}`}>
              {isHindi ? 'शहर / जिला चुनें' : 'Choose city / district'}
            </p>
            <FilterChips items={districtChips} label={isHindi ? 'जिले' : 'Districts'} />
          </>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-4 pb-10">
        <ArticleFeed
          params={params}
          emptyText={isHindi ? `${heading} की कोई खबर अभी नहीं है` : `No news from ${heading} yet`}
        />
      </div>
    </div>
  );
}
